import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";

import { clsx } from "clsx";

import styles from "./RVLearnMoreLink.module.scss";

export type RVLearnMoreLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children"> & {
    /**
     * Destination URL the link should open.
     */
    href: string;
    /**
     * Optional text content to display. Defaults to "Learn More" when `children` is omitted.
     */
    text?: string;
    /**
     * Optional class name applied to the anchor element for local overrides.
     */
    className?: string;
    /**
     * Test ID for use with testing-library's getByTestId.
     */
    "data-testid"?: string;
    /**
     * Optional custom content. When provided, it replaces `text`.
     */
    children?: ReactNode;
};

/**
 * RVLearnMoreLink
 *
 * Minimal external link used throughout the product to direct users to docs or help center articles.
 * Renders a styled anchor that opens in a new tab while maintaining consistent Regal link styling.
 *
 * Remaining anchor props and the forwarded ref are applied to the underlying element so overlay
 * components such as antd `Tooltip` and `Popover`, which clone their child to attach handlers and a
 * ref, work when this link is their trigger. `target` and `rel` stay fixed so external-link
 * behavior remains centralized here.
 */
export const RVLearnMoreLink = forwardRef<HTMLAnchorElement, RVLearnMoreLinkProps>(function RVLearnMoreLink(
    { href, className, text = "Learn More", children, ...anchorProps },
    ref
): JSX.Element {
    return (
        <a
            {...anchorProps}
            ref={ref}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(styles.learnMore, className)}
        >
            {children ?? text}
        </a>
    );
});
